import type { Question } from '../../types';
import { HAB_MH_CONDITION } from './shared-options';

// ---------------------------------------------------------------------------
// Environmental / Pollution Liability Supplement
// Source: Centered Partners MHMF intake — "Environmental" sheet.
// MH parks carry elevated environmental exposure (USTs, septic, lagoons,
// pesticide use). Carriers often require a Phase I ESA before binding.
// Note: UST and septic questions for MH parks are captured in the MH
// supplement; here they gate on habitational only to avoid double-asking.
// ---------------------------------------------------------------------------

const ENV_CONDITION = [
  { field: 'coverage_lines', operator: 'contains' as const, value: 'environmental' },
  ...HAB_MH_CONDITION,
];

const ENV_HAB_ONLY = [
  { field: 'coverage_lines', operator: 'contains' as const, value: 'environmental' },
  { field: 'industry_type', operator: 'eq' as const, value: 'habitational' },
];

export const environmentalQuestions: Question[] = [
  {
    id: 'env_phase1',
    text: 'Has a Phase I Environmental Site Assessment (ESA) been completed?',
    section: 'operations',
    group: 'environmental',
    sort_order: 900,
    field_type: 'select',
    options: [
      { label: 'Yes — for all properties', value: 'all' },
      { label: 'Yes — for some properties', value: 'some' },
      { label: 'No', value: 'none' },
    ],
    required: true,
    field_path: 'environmental.phase1_esa',
    conditions: ENV_CONDITION,
    tags: ['environmental', 'lob'],
  },
  {
    id: 'env_phase2',
    text: 'Did any Phase I findings lead to a Phase II ESA?',
    section: 'operations',
    group: 'environmental',
    sort_order: 910,
    field_type: 'boolean',
    required: true,
    field_path: 'environmental.phase2_esa',
    conditions: [...ENV_CONDITION, { field: 'env_phase1', operator: 'in', value: ['all', 'some'] }],
    tags: ['environmental', 'lob'],
  },
  {
    id: 'env_phase2_detail',
    text: 'Describe the Phase II findings and any remediation',
    section: 'operations',
    group: 'environmental',
    sort_order: 920,
    field_type: 'textarea',
    required: true,
    field_path: 'environmental.phase2_detail',
    conditions: [...ENV_CONDITION, { field: 'env_phase2', operator: 'eq', value: true }],
    tags: ['environmental', 'lob'],
  },
  // USTs / septic for habitational only — MH parks answer these in the MH
  // supplement.
  {
    id: 'env_usts',
    text: 'Any underground storage tanks (USTs) — current or historical?',
    section: 'operations',
    group: 'environmental',
    sort_order: 930,
    field_type: 'boolean',
    required: true,
    field_path: 'environmental.usts',
    conditions: ENV_HAB_ONLY,
    tags: ['environmental', 'lob'],
  },
  {
    id: 'env_usts_detail',
    text: 'Describe the tanks — registration and closure documentation',
    section: 'operations',
    group: 'environmental',
    sort_order: 940,
    field_type: 'textarea',
    required: true,
    field_path: 'environmental.usts_detail',
    conditions: [...ENV_HAB_ONLY, { field: 'env_usts', operator: 'eq', value: true }],
    tags: ['environmental', 'lob'],
  },
  {
    id: 'env_septic',
    text: 'Any septic or lagoon systems?',
    section: 'operations',
    group: 'environmental',
    sort_order: 950,
    field_type: 'boolean',
    required: true,
    field_path: 'environmental.septic',
    conditions: ENV_HAB_ONLY,
    tags: ['environmental', 'lob'],
  },
  {
    id: 'env_septic_detail',
    text: 'Septic / lagoon detail',
    subtext: 'Capacity, last inspection, last pump-out.',
    section: 'operations',
    group: 'environmental',
    sort_order: 960,
    field_type: 'textarea',
    required: true,
    field_path: 'environmental.septic_detail',
    conditions: [...ENV_HAB_ONLY, { field: 'env_septic', operator: 'eq', value: true }],
    tags: ['environmental', 'lob'],
  },
  {
    id: 'env_ast',
    text: 'Any above-ground tanks (propane, fuel, oil)?',
    section: 'operations',
    group: 'environmental',
    sort_order: 970,
    field_type: 'boolean',
    required: true,
    field_path: 'environmental.ast',
    conditions: ENV_HAB_ONLY,
    tags: ['environmental', 'lob'],
  },
  {
    id: 'env_ast_detail',
    text: 'Above-ground tank detail',
    subtext: 'Capacity and whether an SPCC plan is in place.',
    section: 'operations',
    group: 'environmental',
    sort_order: 980,
    field_type: 'textarea',
    required: true,
    field_path: 'environmental.ast_detail',
    conditions: [...ENV_HAB_ONLY, { field: 'env_ast', operator: 'eq', value: true }],
    tags: ['environmental', 'lob'],
  },
  {
    id: 'env_asbestos',
    text: 'Have asbestos surveys been completed for pre-1981 buildings?',
    section: 'operations',
    group: 'environmental',
    sort_order: 990,
    field_type: 'select',
    options: [
      { label: 'Yes — surveys on file', value: 'yes' },
      { label: 'No', value: 'no' },
      { label: 'No pre-1981 buildings', value: 'not_applicable' },
    ],
    required: true,
    field_path: 'environmental.asbestos_surveys',
    conditions: ENV_CONDITION,
    tags: ['environmental', 'lob'],
  },
  {
    id: 'env_lead',
    text: 'Are lead-based paint disclosures / surveys in place for pre-1978 buildings?',
    section: 'operations',
    group: 'environmental',
    sort_order: 1000,
    field_type: 'select',
    options: [
      { label: 'Yes — disclosures and surveys on file', value: 'yes' },
      { label: 'Disclosures only', value: 'disclosures_only' },
      { label: 'No', value: 'no' },
      { label: 'No pre-1978 buildings', value: 'not_applicable' },
    ],
    required: true,
    field_path: 'environmental.lead_surveys',
    conditions: ENV_CONDITION,
    tags: ['environmental', 'lob'],
  },
  {
    id: 'env_mold_history',
    text: 'Any mold remediation in the last 5 years?',
    section: 'operations',
    group: 'environmental',
    sort_order: 1010,
    field_type: 'boolean',
    required: true,
    field_path: 'environmental.mold_history',
    conditions: ENV_CONDITION,
    tags: ['environmental', 'lob'],
  },
  {
    id: 'env_mold_detail',
    text: 'Mold remediation detail',
    subtext: 'By property — vendor and scope.',
    section: 'operations',
    group: 'environmental',
    sort_order: 1020,
    field_type: 'textarea',
    required: true,
    field_path: 'environmental.mold_detail',
    conditions: [...ENV_CONDITION, { field: 'env_mold_history', operator: 'eq', value: true }],
    tags: ['environmental', 'lob'],
  },
  {
    id: 'env_radon',
    text: 'Have radon tests been conducted?',
    section: 'operations',
    group: 'environmental',
    sort_order: 1030,
    field_type: 'boolean',
    required: true,
    field_path: 'environmental.radon_tested',
    conditions: ENV_CONDITION,
    tags: ['environmental', 'lob'],
  },
  {
    id: 'env_pesticide',
    text: 'How is pesticide / herbicide application handled?',
    section: 'operations',
    group: 'environmental',
    sort_order: 1040,
    field_type: 'select',
    options: [
      { label: 'In-house — licensed applicator', value: 'in_house_licensed' },
      { label: 'In-house — no license', value: 'in_house_unlicensed' },
      { label: 'Contracted out', value: 'contracted' },
      { label: 'Not used', value: 'none' },
    ],
    required: true,
    field_path: 'environmental.pesticide_use',
    conditions: ENV_CONDITION,
    tags: ['environmental', 'lob'],
  },
  {
    id: 'env_npdes',
    text: 'Is there a stormwater management plan or NPDES permit?',
    section: 'operations',
    group: 'environmental',
    sort_order: 1050,
    field_type: 'select',
    options: [
      { label: 'Yes — NPDES permit in place', value: 'npdes' },
      { label: 'Stormwater plan only', value: 'plan_only' },
      { label: 'Neither', value: 'none' },
      { label: 'Not applicable', value: 'not_applicable' },
    ],
    required: true,
    field_path: 'environmental.npdes',
    conditions: ENV_CONDITION,
    tags: ['environmental', 'lob'],
  },
];
